import {
  Accordion,
  Box,
  Group,
  Stack,
  Tooltip,
  createStyles,
} from '@mantine/core';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import {
  BiInfoCircle,
  BiMouse,
  BiQuestionMark,
  BiRightArrowAlt,
  BiSolidCheckCircle,
  BiSolidMinusCircle,
  BiSolidXCircle,
} from 'react-icons/bi';

import { useIsXs } from '@unserkunde/enscompare-components/src/components/misc/hooks';

// ChecklistIcon
export const iconTypes = {
  positive: {
    color: 'green.9',
    component: BiSolidCheckCircle,
  },
  negative: {
    color: 'red.9',
    component: BiSolidXCircle,
  },
  warning: {
    color: 'warning.5',
    component: BiSolidCheckCircle, //BiSolidMinusCircle,
  },
  arrow: {
    color: 'gray.8',
    component: BiRightArrowAlt,
  },
  info: {
    color: 'default.5',
    component: BiInfoCircle,
  },
  question: {
    color: 'default.3',
    component: BiQuestionMark,
  },
  mouse: {
    color: 'default.3',
    component: BiMouse,
  },
};

const useIconStyle = createStyles((theme, color: any) => ({
  checklistIcon: {
    flexShrink: 0,
    color:
      (color || 'default.3')
        .split('.')
        .reduce((acc, cur) => acc[cur], theme.colors) + ' !important',
  },
}));

export const ChecklistIcon = ({
  type = 'positive',
  size = null,
}: {
  type?: keyof typeof iconTypes;
  size?: string;
}) => {
  if (!type) return null;

  const settings = iconTypes[type];

  const isXs = useIsXs();
  const baseSize = (isXs ? 10 : 14) * 1.4;

  const { classes } = useIconStyle(settings.color);

  return (
    <Box
      fz={size || `${baseSize}px`}
      className={classes.checklistIcon}
      component={settings.component}
    />
  );
};

export type ChecklistItemProps = {
  type?: keyof typeof iconTypes;

  children?: React.ReactNode;
  tooltip?: any;
} & React.ComponentProps<typeof Group>;

// ChecklistItem
export const ChecklistItem = ({
  type = 'positive',
  children,
  tooltip = null,
  ...rest
}: ChecklistItemProps) => {
  const Renderer = useMemo(
    () =>
      !tooltip
        ? (props) => props.children
        : (props) => (
            <Tooltip
              position="bottom-start"
              offset={3}
              withArrow
              label={tooltip}
            >
              {props.children}
            </Tooltip>
          ),
    [tooltip],
  );

  return (
    <Renderer>
      <Group align="center" spacing={8} noWrap lh={1.15} {...rest}>
        <ChecklistIcon type={type} />
        {children}
      </Group>
    </Renderer>
  );
};

// Checklist
const Checklist = ({
  preview = 2 as number,
  items = [] as any[],
  labelClosed = 'Alle Vorteile anzeigen' as string | null,
  labelOpen = 'Weniger anzeigen' as string | null,
  isOpen = undefined as boolean | undefined,
  onChange = undefined as undefined | ((value: boolean) => void),
  ...rest
}) => {
  const [stateValue, setStateValue] = React.useState();

  const interValue = isOpen !== undefined ? isOpen : stateValue;
  const value = interValue === true ? 'seeMore' : interValue;
  const setValue = onChange !== undefined ? onChange : setStateValue;

  const controlLabel = !value ? labelClosed : labelOpen;

  return (
    <Box {...rest}>
      <Stack spacing={5} justify="flex-start">
        {items.slice(0, preview).map(({ uuid, type, children, tooltip }, i) => (
          <ChecklistItem type={type} key={uuid || i} tooltip={tooltip}>
            {children}
          </ChecklistItem>
        ))}
      </Stack>
      {items.length > preview && (
        // @ts-ignore
        <Accordion unstyled value={value} onChange={setValue}>
          <Accordion.Item value="seeMore">
            <Accordion.Panel pt={6}>
              <Stack spacing={6} justify="flex-start">
                {items.slice(preview).map(({ uuid, type, children }, i) => (
                  <ChecklistItem type={type} key={uuid || i}>
                    {children}
                  </ChecklistItem>
                ))}
              </Stack>
            </Accordion.Panel>
            {controlLabel && (
              <Accordion.Control
                c="primary.9"
                sx={{
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  backgroundColor: 'transparent',
                  border: 'none',
                  gap: 6,
                  fontSize: 16,
                  padding: '8px 0',
                  '& *[class*="-Accordion-chevron"][data-rotate]': {
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                {controlLabel}
              </Accordion.Control>
            )}
          </Accordion.Item>
        </Accordion>
      )}
    </Box>
  );
};

export default Checklist;
